// A small first step (U3): roughly how long a round takes, and how to start one. A waiting round
// offers "About 12 minutes · Start" or "Just the quick ones (about 3 min)". Quick-first is an
// order chosen once, as the round starts (the server stores it with the round), so nothing moves
// under the cursor later. Estimates hide when the owner turns them off in Settings.
import type { CardSummary } from "@shared/api";
import {
  effortWords,
  focusCountWords,
  type RoundEstimate,
  type RoundOrder,
} from "@shared/startEasily";
import { useOwnerTimeZone, useShowEstimates } from "@web/lib/settingsApi";
import { Button } from "@web/ui/Button";
import { Kbd } from "@web/ui/Kbd";
import { estimateOf, focusSlotWords, nextRoundOfferWords, roundEstimateWords } from "./startWords";

/** A row's rough size ("about 1 min"), when estimates show. */
export function RowEffort({ card }: { card: CardSummary }) {
  const show = useShowEstimates();
  return show ? <span data-testid="row-effort">{effortWords(card.effort)}</span> : null;
}

/** A lined-up row's focus time ("Focus time tomorrow at 10 AM"). */
export function RowFocusBlock({ card, now }: { card: CardSummary; now: number }) {
  const zone = useOwnerTimeZone();
  if (!card.focusBlock) return null;
  return <span>Focus time {focusSlotWords(card.focusBlock.start, now, zone)}</span>;
}

/** "about 12 minutes" next to "N in this round". */
export function RoundEstimateText({
  cards,
  cleanups = 0,
}: {
  cards: readonly CardSummary[];
  cleanups?: number;
}) {
  const show = useShowEstimates();
  const words = show ? roundEstimateWords(estimateOf(cards, cleanups)) : null;
  if (!words) return null;
  return (
    <span data-testid="round-estimate" className="text-sm text-muted">
      {words}
    </span>
  );
}

/** Start the waiting round as usual (N), or quick ones first. */
export function NextRoundOffer({
  estimate,
  busy,
  onStart,
  emphasis = "primary",
  size = "sm",
  className,
}: {
  estimate: RoundEstimate;
  busy: boolean;
  onStart: (order?: RoundOrder) => void;
  emphasis?: "primary" | "secondary";
  size?: "sm" | "md";
  className?: string;
}) {
  const show = useShowEstimates();
  const words = nextRoundOfferWords(estimate, show);
  const focus = show ? focusCountWords(estimate.longCount) : null;
  return (
    <div data-testid="next-round-offer" className={className}>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button size={size} variant={emphasis} busy={busy} onClick={() => onStart()}>
          {words.start}
          <Kbd
            className={
              emphasis === "primary"
                ? "border-current bg-transparent text-current opacity-80"
                : undefined
            }
          >
            N
          </Kbd>
        </Button>
        {words.quick ? (
          <Button
            size={size}
            variant="ghost"
            disabled={busy}
            onClick={() => onStart("quick_first")}
          >
            {words.quick}
          </Button>
        ) : null}
      </div>
      {focus ? <p className="mt-1 text-center text-xs text-muted">{focus}</p> : null}
    </div>
  );
}
